import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../utils/axiosConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      if (response.data.success) {
        setUser(response.data.user);
        try {
          localStorage.setItem('auth_user', JSON.stringify(response.data.user));
        } catch (_) {
          /* ignore */
        }
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      const status = error.response?.status;
      // Only clear session on real auth failures — keep user when offline / server down
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('auth_user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      } else {
        try {
          const cached = localStorage.getItem('auth_user');
          if (cached) setUser(JSON.parse(cached));
        } catch (_) {
          /* ignore */
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const applySession = (token, nextUser) => {
    localStorage.setItem('token', token);
    try {
      localStorage.setItem('auth_user', JSON.stringify(nextUser));
    } catch (_) {
      /* ignore */
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(nextUser);
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      if (response.data.success && response.data.needs_client_selection) {
        return {
          success: true,
          needsClientSelection: true,
          selectionToken: response.data.selection_token,
          clients: response.data.clients || [],
          pendingUser: response.data.user || null
        };
      }
      if (response.data.success && response.data.token) {
        applySession(response.data.token, response.data.user);
        return { success: true };
      }
      return {
        success: false,
        message: response.data.message || 'Login failed'
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed. Please check if the server is running.'
      };
    }
  };

  const selectClient = async (selectionToken, clientId) => {
    try {
      const response = await axios.post('/api/auth/select-client', {
        selection_token: selectionToken,
        client_id: clientId
      });
      if (response.data.success && response.data.token) {
        applySession(response.data.token, response.data.user);
        return { success: true };
      }
      return {
        success: false,
        message: response.data.message || 'Failed to select client'
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to select client'
      };
    }
  };

  const switchClient = async (clientId) => {
    try {
      const response = await axios.post('/api/auth/switch-client', { client_id: clientId });
      if (response.data.success && response.data.token) {
        applySession(response.data.token, response.data.user);
        return { success: true, user: response.data.user };
      }
      return {
        success: false,
        message: response.data.message || 'Failed to switch client'
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to switch client'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('auth_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    selectClient,
    switchClient,
    logout,
    refreshUser: fetchUser,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isAdmin: user?.role === 'super_admin' || user?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
